// FASE 05 — S4-B4 (plano docs/19-08-2026): leitura e escrita da planilha de precos.
// Mesmo formato do MOF antigo (site/src/helpers/ExcelToJson e ExportToExcel), para que
// as planilhas que o usuario ja tem continuem valendo.

import * as XLSX from "xlsx";
import FileSaver from "file-saver";
import { MaterialEnum } from "../../types/materialEnum";

const TIPO_ARQUIVO_XLSX =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";

export interface ILinhaPlanilhaComponente {
  Codigo: string;
  Valor: number;
}

export interface ILinhaPlanilhaVidro {
  Codigo: string;
  Cor: string;
  PrecoM2: number;
}

/**
 * Le a primeira aba do arquivo e devolve as linhas em JSON — exatamente o que o endpoint
 * ImportarPlanilha do legado espera no campo `Conteudo`.
 */
export const planilhaParaJson = (arquivo: File): Promise<any[]> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const dados = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(dados, { type: "array" });
        const primeiraAba = workbook.SheetNames[0];

        if (!primeiraAba) {
          resolve([]);
          return;
        }

        resolve(XLSX.utils.sheet_to_json(workbook.Sheets[primeiraAba]));
      } catch (erro) {
        reject(erro);
      }
    };

    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(arquivo);
  });

/** Gera e baixa o .xlsx com os precos atuais da tabela. */
export const baixarPlanilhaPrecos = (
  linhas: Array<{
    codigo: string;
    cor: string;
    descricao: string;
    valor: number;
    taxa?: number | null;
    medida?: string | null;
    tamanhoBarra?: number | null;
  }>,
  tipoMaterial: number,
  nomeArquivo: string
): void => {
  // Plano 30/08/2026: a planilha exportada precisa voltar importável, então sai com as MESMAS
  // colunas do modelo de cada tipo — Taxa nos Acabamentos (Frente 10 / TP-20) e Cor + Largura +
  // Altura nos Kits (Frente 11 / TP-18, onde o preço é por kit+medida+cor).
  const dados = montarLinhasDaPlanilha(linhas, tipoMaterial);

  const planilha = XLSX.utils.json_to_sheet(dados);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, planilha, "Precos");

  const buffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  FileSaver.saveAs(new Blob([buffer], { type: TIPO_ARQUIVO_XLSX }), `${nomeArquivo}.xlsx`);
};

/** "1000 X 1000" -> { largura: 1000, altura: 1000 }. Devolve vazio quando não há medida. */
const separarMedida = (medida?: string | null): { largura: string; altura: string } => {
  const partes = (medida ?? "").split(/\s*[xX]\s*/);
  return partes.length === 2
    ? { largura: partes[0].trim(), altura: partes[1].trim() }
    : { largura: "", altura: "" };
};

const montarLinhasDaPlanilha = (
  linhas: Array<{
    codigo: string;
    cor: string;
    valor: number;
    taxa?: number | null;
    medida?: string | null;
    tamanhoBarra?: number | null;
  }>,
  tipoMaterial: number
): Array<Record<string, string | number>> => {
  if (tipoMaterial === MaterialEnum.VIDRO)
    return linhas.map((l) => ({ Codigo: l.codigo, Cor: l.cor ?? "", PrecoM2: l.valor }));

  // TP-20: Acabamentos usam Código, Taxa e Preço — a Taxa não é obrigatória, então sai em
  // branco quando não informada (e assim volta a ser importada sem apagar o que está gravado).
  if (tipoMaterial === MaterialEnum.ACABAMENTO)
    return linhas.map((l) => ({
      Codigo: l.codigo,
      Taxa: l.taxa ?? "",
      Preco: l.valor,
    }));

  // TP-18: Kits têm um preço por combinação, então a planilha identifica a combinação.
  if (tipoMaterial === MaterialEnum.KIT)
    return linhas.map((l) => {
      const { largura, altura } = separarMedida(l.medida);
      return {
        Codigo: l.codigo,
        Cor: l.cor ?? "",
        Largura: largura,
        Altura: altura,
        Valor: l.valor,
      };
    });

  // Plano 08/09/2026: Perfis levam TamanhoBarra — preenchido só nos perfis em BR (uma linha por
  // tamanho); vazio nos demais, e o importador ignora a coluna para eles.
  if (tipoMaterial === MaterialEnum.PERFIL)
    return linhas.map((l) => ({
      Codigo: l.codigo,
      TamanhoBarra: l.tamanhoBarra ?? "",
      Valor: l.valor,
    }));

  return linhas.map((l) => ({ Codigo: l.codigo, Valor: l.valor }));
};

/** Modelo em branco, com os cabecalhos que o importador reconhece para cada tipo. */
export const baixarModeloPlanilha = (tipoMaterial: number): void => {
  const modelo =
    tipoMaterial === MaterialEnum.VIDRO
      ? [{ Codigo: "", Cor: "", PrecoM2: 0 }]
      : tipoMaterial === MaterialEnum.ACABAMENTO
      ? [{ Codigo: "", Taxa: "", Preco: 0 }]
      : tipoMaterial === MaterialEnum.KIT
      ? [{ Codigo: "", Cor: "", Largura: "", Altura: "", Valor: 0 }]
      : tipoMaterial === MaterialEnum.PERFIL
      ? [{ Codigo: "", TamanhoBarra: "", Valor: 0 }]
      : [{ Codigo: "", Valor: 0 }];

  const planilha = XLSX.utils.json_to_sheet(modelo);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, planilha, "Precos");

  const buffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  FileSaver.saveAs(new Blob([buffer], { type: TIPO_ARQUIVO_XLSX }), "modelo-tabela-precos.xlsx");
};
